import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Feather, Check, Gift } from "lucide-react";
import { captureSource, getSignupSource, getRefCode } from "../lib/source";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Join() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    captureSource();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/subscribe`, {
        email,
        name,
        source: getSignupSource(),
        ref: getRefCode(),
      });
      localStorage.setItem("fb_sub_email", email.toLowerCase());
      setDone(true);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-card border border-border rounded-2xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="px-8 pt-10 pb-6 text-center">
            <img
              src="/images/elizabeth-portrait.jpg"
              alt="Elizabeth Neal"
              className="w-20 h-20 rounded-full object-cover mx-auto mb-4 border-2 border-brand-accent/30"
            />
            <p className="overline mb-2">Freelanceblogger</p>
            <h1 className="font-cormorant text-3xl font-semibold text-foreground mb-3">
              Words that work. Weekly.
            </h1>
            <p className="font-manrope text-sm text-muted-foreground leading-relaxed">
              Join Elizabeth Neal's free newsletter on writing and content that attracts
              customers — blog posts, newsletters, and strategies that build authority.
            </p>
          </div>

          {/* Form / Success */}
          <div className="px-8 pb-10">
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center"
                data-testid="join-success"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-50 dark:bg-green-900/20 mb-4">
                  <Check size={24} className="text-green-600" />
                </div>
                <h2 className="font-cormorant text-2xl font-semibold text-foreground mb-2">
                  You're in — welcome!
                </h2>
                <p className="font-manrope text-xs text-muted-foreground mb-6">
                  Check your inbox for a welcome note from Elizabeth.
                </p>
                <Link
                  to="/referrals"
                  data-testid="join-referral-cta"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-accent text-white font-manrope text-sm font-medium hover:bg-brand-accent-hover transition-colors"
                >
                  <Gift size={15} />
                  Earn free premium by sharing
                </Link>
                <p className="font-manrope text-xs text-muted-foreground mt-4">
                  <Link to="/" className="text-brand-accent hover:underline" data-testid="join-read-link">
                    Start reading →
                  </Link>
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3" data-testid="join-form">
                <input
                  type="text"
                  placeholder="First name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="join-name-input"
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background font-manrope text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
                />
                <input
                  type="email"
                  required
                  placeholder="Your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="join-email-input"
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background font-manrope text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="join-submit-btn"
                  className="w-full py-3.5 rounded-full bg-brand-accent text-white font-manrope text-sm font-semibold hover:bg-brand-accent-hover disabled:opacity-60 transition-colors"
                >
                  {submitting ? "Joining..." : "Subscribe free"}
                </button>
                <p className="font-manrope text-[11px] text-muted-foreground text-center pt-1">
                  Free forever. Unsubscribe anytime. No spam — just good writing.
                </p>
              </form>
            )}
          </div>
        </div>

        <p className="text-center mt-6">
          <Link
            to="/"
            data-testid="join-home-link"
            className="inline-flex items-center gap-1.5 font-manrope text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <Feather size={12} className="text-brand-accent" />
            freelanceblogger.club
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
